import { useQuery } from "@tanstack/react-query";

import { apiClient } from "@/api/client";
import { ApiError } from "@/api/errors";
import type { components } from "@/api/schema";

import { stockKeys } from "./query-keys";

type Marketplace = components["parameters"]["Marketplace"];
type SellerRefResult = components["schemas"]["SellerRefResult"];

/**
 * "Check Sellername" read-only lookup (PRD v1.8 §4.6.3.2-D).
 *
 * Resolves the (seller_id, product_id, seller_name) listing identities for a
 * (sku, marketplace). Purely READ — no mutation, no rate limit.
 * A single (sku, marketplace) may map to more than one listing, so `items` is
 * an array; an empty array is a valid 200 meaning "no listing found".
 *
 * While the real marketplace adapter is undelivered it returns HTTP
 * 501 `{ "error": "not_implemented" }`. We surface that distinctly via the
 * derived `isNotImplemented` flag so the UI can show a graceful "not available
 * yet" state instead of a generic error.
 *
 * `enabled` should be the modal's open state — the query only fires for a
 * selected (sku, marketplace).
 */
export function useSellerRef(sku: string, marketplace: Marketplace | "", enabled = true) {
  const query = useQuery<SellerRefResult, Error>({
    queryKey: stockKeys.sellerRef(sku, marketplace),
    queryFn: async () => {
      const { data, error } = await apiClient.GET("/v1/stock/{sku}/seller-ref", {
        params: {
          path: { sku },
          query: { marketplace: marketplace as Marketplace },
        },
      });
      if (error) throw error;
      return data!;
    },
    enabled: enabled && !!sku && !!marketplace,
    staleTime: 30_000,
    // Manual lookup with an explicit Retry button in the modal — no auto-retry.
    // Notably 501 not_implemented is a deterministic "not built yet" anyway.
    retry: false,
  });

  const isNotImplemented = query.error instanceof ApiError && query.error.status === 501;

  return { ...query, isNotImplemented };
}
